import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "../../../../../components/ui-comp/input";
import { cn, convertToUTC } from "../../../../../lib/utils";
import { z } from "zod";
import { Button } from "../../../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { createScan } from "../../../../../helpers/mutations/targets";
import { useGetAllCredentials } from "../../../../../helpers/queries/credentials/useGetCredentials";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../../components/ui/radio-group";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../../components/ui/form";
import {
  useGetConfigs,
  useGetPorts,
  useGetScanners,
} from "../../../../../helpers/queries/targets/useGetTargets";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";

const RunScanModal = ({ data: target, openScan, setOpenScan, assetList }) => {
  const rawTarget = assetList ? target?.target?.data : target?.target;

  // const [portType, setPortType] = useState("");
  const [scheduleScan, setScheduleScan] = useState(false);
  const [autoStartScan, setAutoStartScan] = useState(false);
  const { data: ports } = useGetPorts();
  const { data: scanners } = useGetScanners();
  const { data: configs } = useGetConfigs();
  let { data: credentials } = useGetAllCredentials();

  credentials = credentials?.data ?? [];

  const portOptions = (ports ?? [])?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const selectedPort = portOptions?.find(
    (type) => type.label === "All IANA assigned TCP and UDP"
  );

  const scannerOptions = (scanners ?? [])?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const configOptions = (configs ?? [])?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const selectedConfig = configOptions?.find(
    (type) => type.label === "Full and fast"
  );

  const handleClose = () => {
    setOpenScan(false);
  };

  const base = {
    // ports: z.string({ required_error: "Port is required" }).min(1, "Please select a port"), // String validation
    scanners: z
      .string({ required_error: "Scanner is required" })
      .min(1, "Please select a scanner"), // String validation
    // configs: z.string({ required_error: "Config is required" }).min(1, "Please select a config"), // String validation
    credential: z.string().optional(), // (optional)
    scanType: z.string().optional(), // (optional)
  };

  const scheduleField = {
    scheduleType: z.string().min(1, "Please select a type"),
    startDate: z.string().min(1, "Please select a date"),
    startTime: z.string().min(1, "Please select a time"),
  };

  const formSchema = scheduleScan
    ? z.object({ ...base, ...scheduleField })
    : z.object(base);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // ports: "",
      scanners: "",
      credential: "",
      // configs: "",
      // is_internal: z.boolean(),
      scheduleType: "",
      startDate: "",
      startTime: "",
    },
  });

  const {
    register,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (target) {
      form.reset({
        name: target.name || "",
        target: Array.isArray(rawTarget)
          ? rawTarget.join(",") // convert array → string
          : rawTarget || "", // leave string as-is
      });
    }
    // eslint-disable-next-line
  }, [target, form, assetList]);

  const targetType = target?.asset_type ?? "";

  const filteredScannerOptions =
    targetType === "web"
      ? scannerOptions?.filter((opt) => opt.label === "TuringTower Web Scanner")
      : targetType === "infrastructure"
      ? scannerOptions?.filter(
          (opt) =>
            opt.label === "TuringTower Default" ||
            opt.label === "TuringTower Agent"
        )
      : [];

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate, isPending } = useMutation({
    mutationFn: createScan,
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries({
        queryKey: ["scans"],
      });
      queryClient.invalidateQueries({ queryKey: ["asset_groups"] });
      toast("Successful");
      navigate("/dashboard/scan-history");
    },
    onError: (data) => {
      console.log(data);
      toast(data.message);
    },
  });

  const onSubmit = (data) => {
    const payload = {
      port_type: selectedPort?.label,
      ports: selectedPort?.value,
      config_id: selectedConfig?.value,
      target_id: target?.id,
      credential_id: Number(data?.credential),
      autoStartScan: autoStartScan,
      scanner_id: data?.scanners,
      ...(scheduleScan && {
        isScheduled: true,
        schedule: {
          type: data?.scheduleType,
          startDate: data?.startDate,
          startTime: convertToUTC(data?.startTime?.slice(0, 5)),
        },
      }),
    };
    mutate(payload);
  };

  return (
    <Dialog open={openScan} onOpenChange={setOpenScan}>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Create Scan
          </DialogTitle>
          <DialogDescription>
            Scan to check for known vulnerabilities or misconfigurations.
          </DialogDescription>
        </DialogHeader>
        <hr />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Name"
              name="name"
              type="text"
              register={register}
              value={target?.name}
              disabled={true}
              placeholder="Enter name"
              className={cn(
                errors.name &&
                  "border-[#e84539] focus-visible:ring-[#ec5247] ring:ring-[#FEE4E2]"
              )}
            />
            <Input
              label="Domain(s) or IP Address(es)"
              name="target"
              type="text"
              register={register}
              value={rawTarget}
              disabled={true}
              placeholder="Enter domain"
              className={cn(
                errors.target &&
                  "border-[#e84539] focus-visible:ring-[#ec5247] ring:ring-[#FEE4E2]"
              )}
            />

            {/* <FormField
                  control={form.control}
                  name="ports"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Ports</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value); // Update the form field value
                          const selectedLabel = portOptions.find(
                            (option) => option.value === value
                          )?.label;
                          setPortType(selectedLabel); // Update the selected port label
                        }}
                        value={field?.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select a port" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {portOptions?.map((type) =>
                            type.label === "All IANA assigned TCP and UDP" ? (
                              <SelectItem key={type.label} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ) : null
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
            <FormField
              control={form.control}
              name="scanners"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Scanners</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field?.value}
                    // disabled={disabled}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select a scanner" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredScannerOptions?.map((type) => (
                        <SelectItem key={type.label} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* <FormField
                  control={form.control}
                  name="configs"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Configs</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field?.value}
                        // disabled={disabled}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select config" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {configOptions?.map((type) =>
                            type.label === "Full and fast" ? (
                              <SelectItem key={type.label} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ) : null
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

            <FormField
              control={form.control}
              name="credential"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Credential (Optional)</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? "" : value)
                    }
                    value={field.value || "none"}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select credential" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No option</SelectItem>
                      {credentials?.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name ?? "invalid"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="scanType"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Scan Type</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => {
                        field.onChange(value);
                        setScheduleScan(value === "scheduled");
                        setAutoStartScan(value === "automatic");
                      }}
                      value={field.value}
                      className="grid grid-cols-2"
                    >
                      <FormItem className="flex items-center gap-3 cursor-pointer">
                        <FormControl>
                          <RadioGroupItem value="automatic" className="mt-2" />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer">
                          Automatic
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-3 cursor-pointer">
                        <FormControl>
                          <RadioGroupItem value="scheduled" className="mt-2" />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer">
                          Scheduled
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {scheduleScan && (
              <>
                <FormField
                  control={form.control}
                  name="scheduleType"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Schedule Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field?.value}
                        // disabled={disabled}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select a type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="one-off">Once</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="md:flex gap-4 justify-between items-center">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => {
                      return (
                        <FormItem className="flex flex-col md:w-1/2 mb-4">
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col md:w-1/2 mb-4">
                        <FormLabel>Start time</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="time"
                            id="time-picker"
                            step="1"
                            className="bg-background [&::-webkit-calendar-picker-indicator]:mr-0 "
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white"
                loading={isPending}
              >
                Create
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RunScanModal;
